import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SystemDisclaimers from "@/components/SystemDisclaimers";
import {
  Globe,
  Users,
  Shield,
  AlertTriangle,
  TrendingUp,
  Clock,
  MapPin,
  ExternalLink,
  RefreshCw,
  Info
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [overviewRes, logsRes, timelineRes, countriesRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/logs?limit=5`, { headers }),
        axios.get(`${API}/analytics/timeline?days=7`, { headers }),
        axios.get(`${API}/analytics/countries`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setRecentLogs(logsRes.data);
      setTimeline(timelineRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const getThreatBadge = (level) => {
    const variants = {
      safe: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      suspicious: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      threat: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[level] || variants.safe;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header with Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Globe className="w-8 h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}!
            </h1>
          </div>
          <p className="text-muted-foreground text-sm">Real-time network monitoring overview</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="gap-2"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border card-hover" data-testid="stat-total-logs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Logs</p>
                <p className="text-2xl font-mono font-bold mt-1">{overview?.total_logs || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border card-hover" data-testid="stat-unique-ips">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Unique IPs</p>
                <p className="text-2xl font-mono font-bold mt-1">{overview?.unique_ips || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border card-hover" data-testid="stat-threats">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Threats</p>
                <p className="text-2xl font-mono font-bold mt-1 text-red-500">{overview?.threats || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-md bg-red-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border card-hover" data-testid="stat-alerts">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Alerts</p>
                <p className="text-2xl font-mono font-bold mt-1 text-amber-500">{overview?.unresolved_alerts || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Timeline Chart */}
        <Card className="lg:col-span-2 bg-card/50 border-border" data-testid="timeline-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Visitor Timeline (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] w-full">
              {timeline.length > 0 ? (
                <ResponsiveContainer width="99%" height={200}>
                  <AreaChart data={timeline}>
                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#71717A', fontSize: 11 }}
                      tickFormatter={(value) => value.slice(5)}
                      axisLine={{ stroke: '#27272A' }}
                    />
                    <YAxis 
                      tick={{ fill: '#71717A', fontSize: 11 }}
                      axisLine={{ stroke: '#27272A' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid #27272A',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#0EA5E9" 
                      fill="url(#colorCount)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Countries */}
        <Card className="bg-card/50 border-border" data-testid="top-countries">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Top Countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {countries.length > 0 ? (
                countries.slice(0, 5).map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm">{country.country || "Unknown"}</span>
                    <Badge variant="outline" className="font-mono text-xs">
                      {country.count}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground text-sm text-center py-8">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Logs */}
      <Card className="bg-card/50 border-border" data-testid="recent-logs">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Recent Activity
          </CardTitle>
          <Link to="/logs">
            <Button variant="ghost" size="sm" className="text-xs gap-1" data-testid="view-all-logs-btn">
              View All
              <ExternalLink className="w-3 h-3" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2">IP Address</th>
                  <th className="text-left px-4 py-2 hidden sm:table-cell">Location</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Browser</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2 hidden lg:table-cell">Time</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.length > 0 ? (
                  recentLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-secondary/30 transition-colors">
                      <td className="px-4 py-3">
                        <Link 
                          to={`/logs/${log.id}`} 
                          className="font-mono text-primary hover:underline"
                          data-testid={`log-ip-${log.id}`}
                        >
                          {log.ip_address}
                        </Link>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">
                        {log.city && log.country ? `${log.city}, ${log.country}` : log.country || "Unknown"}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                        {log.browser || "Unknown"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${getThreatBadge(log.threat_level)}`}>
                          {log.threat_level}
                        </Badge>
                        {log.detection_confidence === "low" && (log.is_vpn || log.is_proxy) && (
                          <span className="ml-1 text-[10px] text-muted-foreground">(~30%)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell font-mono text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                      No logs recorded yet. Use the tracking endpoint to start logging IPs.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Today's Visitors</p>
            <p className="text-xl font-mono font-bold mt-1">{overview?.today_visitors || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Suspicious</p>
            <p className="text-xl font-mono font-bold mt-1 text-amber-500">{overview?.suspicious || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Blacklisted</p>
            <p className="text-xl font-mono font-bold mt-1">{overview?.blacklist_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Whitelisted</p>
            <p className="text-xl font-mono font-bold mt-1">{overview?.whitelist_count || 0}</p>
          </CardContent>
        </Card>
      </div>
      {/* System Disclaimers */}
      <SystemDisclaimers />
    </div>
  );
};

export default DashboardPage;
